import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const PAGE_TITLES = {
  '/': {
    en: 'Afghan Gym | افغان جم',
    fa: 'Afghan Gym | افغان جم',
  },
  '/landing': {
    en: 'Afghan Gym | افغان جم',
    fa: 'Afghan Gym | افغان جم',
  },
  '/login': {
    en: 'Login | Afghan Gym',
    fa: 'ورود | افغان جم',
  },
  '/register': {
    en: 'Register | Afghan Gym',
    fa: 'ثبت نام | افغان جم',
  },
  '/dashboard': {
    en: 'Dashboard | Afghan Gym',
    fa: 'داشبورد | افغان جم',
  },
  '/features': {
    en: 'Features | Afghan Gym',
    fa: 'ویژگی ها | افغان جم',
  },
  '/about': {
    en: 'About | Afghan Gym',
    fa: 'درباره ما | افغان جم',
  },
  '/pricing': {
    en: 'Pricing | Afghan Gym',
    fa: 'قیمت گذاری | افغان جم',
  },
};

const PAGE_DESCRIPTIONS = {
  '/': {
    en: 'Comprehensive gym management software for Afghan gyms. Manage members, trainers, programs, and payments efficiently.',
    fa: 'نرم افزار جامع مدیریت باشگاه برای باشگاه های افغانستان. مدیریت اعضا، مربیان، برنامه ها و پرداخت ها به صورت کارآمد.',
  },
  '/landing': {
    en: 'Comprehensive gym management software for Afghan gyms. Manage members, trainers, programs, and payments efficiently.',
    fa: 'نرم افزار جامع مدیریت باشگاه برای باشگاه های افغانستان. مدیریت اعضا، مربیان، برنامه ها و پرداخت ها به صورت کارآمد.',
  },
  '/login': {
    en: 'Sign in to Afghan Gym - Access your gym management dashboard to manage your gym operations.',
    fa: 'ورود به افغان جم - دسترسی به داشبورد مدیریت باشگاه برای مدیریت عملیات باشگاه خود.',
  },
  '/register': {
    en: 'Create an account with Afghan Gym - Start managing your gym with our comprehensive management software.',
    fa: 'ایجاد حساب در افغان جم - شروع مدیریت باشگاه خود با نرم افزار جامع مدیریت ما.',
  },
  '/dashboard': {
    en: 'Gym Dashboard - Manage your gym members, trainers, programs, and track performance metrics.',
    fa: 'داشبورد باشگاه - مدیریت اعضا، مربیان، برنامه های باشگاه و پیگیری شاخص های عملکرد.',
  },
  '/features': {
    en: 'Explore Afghan Gym features - Member management, trainer scheduling, workout programs, payment processing, and more.',
    fa: 'ویژگی های افغان جم را کاوش کنید - مدیریت اعضا، زمان بندی مربیان، برنامه های ورزشی، پرداخت و بیشتر.',
  },
  '/about': {
    en: 'About Afghan Gym - Learn about our mission to revolutionize gym management in Afghanistan with modern technology.',
    fa: 'درباره افغان جم - درباره ماموریت ما برای انقلاب در مدیریت باشگاه در افغانستان با تکنولوژی مدرن بدانید.',
  },
  '/pricing': {
    en: 'Afghan Gym Pricing - Affordable gym management software plans for gyms of all sizes in Afghanistan.',
    fa: 'قیمت گذاری افغان جم - طرح های مقرون به صرفه نرم افزار مدیریت باشگاه برای باشگاه های همه اندازه در افغانستان.',
  },
};

const PAGE_KEYWORDS = {
  '/': {
    en: 'gym management software, Afghanistan gym software, gym membership management, fitness center software, gym administration',
    fa: 'نرم افزار مدیریت باشگاه، نرم افزار باشگاه افغانستان، مدیریت عضویت باشگاه، نرم افزار مرکز تناسب اندام، اداره باشگاه',
  },
  '/landing': {
    en: 'gym management software, Afghanistan gym software, gym membership management, fitness center software, gym administration',
    fa: 'نرم افزار مدیریت باشگاه، نرم افزار باشگاه افغانستان، مدیریت عضویت باشگاه، نرم افزار مرکز تناسب اندام، اداره باشگاه',
  },
  '/login': {
    en: 'gym login, Afghan Gym sign in, gym management dashboard, gym software login',
    fa: 'ورود باشگاه، ورود به افغان جم، داشبورد مدیریت باشگاه، ورود نرم افزار باشگاه',
  },
  '/register': {
    en: 'gym registration, Afghan Gym signup, gym management software account, gym software register',
    fa: 'ثبت نام باشگاه، ثبت نام در افغان جم، حساب نرم افزار مدیریت باشگاه، ثبت نام نرم افزار باشگاه',
  },
  '/dashboard': {
    en: 'gym dashboard, gym management panel, member dashboard, trainer dashboard, gym administration',
    fa: 'داشبورد باشگاه، پنل مدیریت باشگاه، داشبورد عضو، داشبورد مربی، اداره باشگاه',
  },
  '/features': {
    en: 'gym software features, member management, trainer scheduling, workout programs, payment processing',
    fa: 'ویژگی های نرم افزار باشگاه، مدیریت اعضا، زمان بندی مربیان، برنامه های ورزشی، پرداخت',
  },
  '/about': {
    en: 'Afghan Gym about us, gym management software Afghanistan, fitness software company',
    fa: 'درباره افغان جم، نرم افزار مدیریت باشگاه افغانستان، شرکت نرم افزار تناسب اندام',
  },
  '/pricing': {
    en: 'gym software pricing, Afghan Gym cost, gym management software price, affordable gym software',
    fa: 'قیمت نرم افزار باشگاه، هزینه افغان جم، قیمت نرم افزار مدیریت باشگاه، نرم افزار باشگاه مقرون به صرفه',
  },
};

export default function SEO() {
  const { i18n } = useTranslation();
  const location = useLocation();
  
  const lang = i18n.language === 'fa' || i18n.language === 'ps' ? 'fa' : 'en';
  const dir = lang === 'fa' ? 'rtl' : 'ltr';
  
  const getPageTitle = () => {
    const titles = PAGE_TITLES[location.pathname] || PAGE_TITLES['/'];
    return titles[lang] || titles.en;
  };

  const getPageDescription = () => {
    const descriptions = PAGE_DESCRIPTIONS[location.pathname] || PAGE_DESCRIPTIONS['/'];
    return descriptions[lang] || descriptions.en;
  };

  const getPageKeywords = () => {
    const keywords = PAGE_KEYWORDS[location.pathname] || PAGE_KEYWORDS['/'];
    return keywords[lang] || keywords.en;
  };

  const getBreadcrumbSchema = () => {
    const breadcrumbs = [
      { name: lang === 'fa' ? 'خانه' : 'Home', item: window.location.origin },
    ];

    if (location.pathname !== '/') {
      const pathParts = location.pathname.split('/').filter(Boolean);
      let currentPath = '';
      pathParts.forEach((part, index) => {
        currentPath += `/${part}`;
        const pageName = PAGE_TITLES[currentPath]?.[lang] || PAGE_TITLES[currentPath]?.en || part;
        breadcrumbs.push({
          name: pageName.split('|')[0].trim(),
          item: `${window.location.origin}${currentPath}`,
        });
      });
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: crumb.item,
      })),
    };
  };

  const getWebSiteSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: lang === 'fa' ? 'افغان جم' : 'Afghan Gym',
    url: window.location.origin,
    description: getPageDescription(),
    inLanguage: lang,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${window.location.origin}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  });

  const getFAQSchema = () => {
    if (location.pathname !== '/' && location.pathname !== '/landing') return null;

    const faqs = lang === 'fa' ? [
      {
        question: 'نرم افزار مدیریت باشگاه چیست؟',
        answer: 'نرم افزار مدیریت باشگاه یک سیستم دیجیتال است که به مالکان باشگاه کمک می کند تا اعضا، پرداخت ها، برنامه های تمرینی و عملیات روزانه را به صورت کارآمد مدیریت کنند. افغان جم یک سیستم مدیریت باشگاه جامع است که به طور خاص برای باشگاه های افغان طراحی شده است.'
      },
      {
        question: 'آیا این نرم افزار برای باشگاه های کوچک مناسب است؟',
        answer: 'بله، افغان جم برای باشگاه های همه اندازه مناسب است، از باشگاه های کوچک محلی تا باشگاه های بزرگ. طرح های قیمت گذاری ما مقرون به صرفه هستند و به نیازهای باشگاه های افغان پاسخ می دهند.'
      },
      {
        question: 'چه زبان هایی پشتیبانی می شوند؟',
        answer: 'افغان جم از فارسی، پشتو و انگلیسی پشتیبانی می کند، که آن را برای کاربران در سراسر افغانستان مناسب می کند.'
      },
      {
        question: 'چگونه می توانم شروع کنم؟',
        answer: 'به سادگی یک حساب کاربری ایجاد کنید، اطلاعات باشگاه خود را وارد کنید و شروع به مدیریت اعضا، مربیان و پرداخت ها کنید. ما همچنین یک دوره آزمایشی رایگان ارائه می دهیم.'
      }
    ] : [
      {
        question: 'What is gym management software?',
        answer: 'Gym management software is a digital system that helps gym owners manage members, payments, workout programs, and daily operations efficiently. Afghan Gym is a comprehensive gym management system specifically designed for Afghan gyms.'
      },
      {
        question: 'Is this software suitable for small gyms?',
        answer: 'Yes, Afghan Gym is suitable for gyms of all sizes, from small local gyms to large fitness centers. Our pricing plans are affordable and cater to the needs of Afghan gyms.'
      },
      {
        question: 'What languages are supported?',
        answer: 'Afghan Gym supports Dari, Pashto, and English, making it accessible for users across Afghanistan.'
      },
      {
        question: 'How do I get started?',
        answer: 'Simply create an account, enter your gym information, and start managing members, trainers, and payments. We also offer a free trial period.'
      }
    ];

    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    };
  };

  return (
    <Helmet>
      <html lang={lang} dir={dir} />
      <title>{getPageTitle()}</title>
      <meta name="description" content={getPageDescription()} />
      <meta name="keywords" content={getPageKeywords()} />
      <link rel="canonical" href={`${window.location.origin}${location.pathname}`} />
      <meta property="og:title" content={getPageTitle()} />
      <meta property="og:description" content={getPageDescription()} />
      <meta property="og:url" content={`${window.location.origin}${location.pathname}`} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={lang === 'fa' ? 'fa_IR' : 'en_US'} />
      <script type="application/ld+json">
        {JSON.stringify(getBreadcrumbSchema())}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(getWebSiteSchema())}
      </script>
      {getFAQSchema() && (
        <script type="application/ld+json">
          {JSON.stringify(getFAQSchema())}
        </script>
      )}
    </Helmet>
  );
}
